import { Text, View, useWindowDimensions } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { LegendList } from "@legendapp/list";
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SearchBox from '@/components/General/inputs/SearchBox';
import usePlatform from '@/utils/hooks/usePlatform';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useRouter, useNavigation } from 'expo-router';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import DropdownMenu from '@/components/General/DropdownMenu';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import TrainingModuleCard from '@/components/Training/TrainingModuleCard';

export default function TrainingScreen() {
    const [searchText, setSearchText] = useState("");
    const [tabIndex, setTabIndex] = useState(0);
    const [tabRoutes] = useState([
        { key: 'modules', title: 'Modules' },
        { key: 'stats', title: 'Overview' }
    ]);
    const layout = useWindowDimensions();

    const { theme: { colors } } = useUnistyles();
    const { isLandscape, isNative } = usePlatform();
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();

    const { data: loggedInUser, isLoading: isLoadingLoggedInUser } = Query.people.loggedInUser();

    const { data: modules, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = Query.trainingModules.all({
        searchTerm: searchText,
        options: { enabled: !isLoadingLoggedInUser }
    });

    const { data: overviewStats } = Query.trainingOverview.stats({ options: { enabled: tabIndex === 1 } });

    useEffect(() => {
        const options = [];

        if (loggedInUser?.can?.("Create Training")) {
            options.push({ name: "New Training Module", onSelect: () => router.push("/training/create") });
        }

        navigation.setOptions({
            title: "Training",
            headerRight: options.length > 0 ? () => <DropdownMenu testID="HeaderMenu" options={options} /> : undefined
        });
    }, [navigation, loggedInUser]);

    const allModules = modules?.pages?.flatMap(page => page.data) || [];

    // Sort: Active first, then alphabetical by name
    const sortedModules = useMemo(() => {
        return [...allModules].sort((a, b) => {
            const aActive = a.status === 'ACTIVE' ? 0 : 1;
            const bActive = b.status === 'ACTIVE' ? 0 : 1;
            if (aActive !== bActive) return aActive - bActive;
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [allModules]);

    const renderModuleItem = useCallback(({ item }) => (
        <TrainingModuleCard module={item} />
    ), []);

    const renderOverviewTab = () => {
        if (!overviewStats) return <ActivityIndicator size="large" style={{ marginTop: 40 }} />;

        const stats = [
            { label: "Active Modules", value: overviewStats.activeModules },
            { label: "Total Enrollments", value: overviewStats.totalEnrollments },
            { label: "Active Students", value: overviewStats.activeEnrollments },
            { label: "Completed", value: overviewStats.completedEnrollments },
            { label: "Dropped/Unenrolled", value: overviewStats.droppedEnrollments },
            { label: "Completion Rate", value: `${overviewStats.overallCompletionRate}%` },
        ];

        return (
            <View style={styles.overviewContainer}>
                <Text style={styles.overviewTitle}>Training Overview</Text>
                <Text style={styles.overviewSubtitle}>High-level statistics across all training modules</Text>
                <View style={styles.statsGrid}>
                    {stats.map((stat, i) => (
                        <View key={i} style={styles.statCard}>
                            <Text style={styles.statValue}>{stat.value}</Text>
                            <Text style={styles.statLabel}>{stat.label}</Text>
                        </View>
                    ))}
                </View>
            </View>
        );
    };

    return (
        <View style={styles.container}>
            <TabView
                swipeEnabled={isNative}
                navigationState={{ index: tabIndex, routes: tabRoutes }}
                onIndexChange={setTabIndex}
                initialLayout={{ width: layout.width }}
                renderTabBar={(props) => <ReachTabBar {...props} />}
                lazy
                renderScene={({ route }) => {
                    switch (route.key) {
                        case 'stats':
                            return renderOverviewTab();
                        case 'modules':
                            return (
                                <View style={styles.listContainer}>
                                    <View style={styles.listContent(isLandscape)}>
                                        <View style={styles.searchBar}>
                                            <SearchBox
                                                searchText={searchText}
                                                placeholder="Search modules..."
                                                onChangeText={setSearchText}
                                                textChangeDelay={0}
                                            />
                                        </View>
                                        <LegendList
                                            recycleItems
                                            keyExtractor={(item) => item.id}
                                            estimatedItemSize={120}
                                            showsVerticalScrollIndicator={false}
                                            data={isLoading ? [] : sortedModules}
                                            renderItem={renderModuleItem}
                                            onEndReachedThreshold={0.1}
                                            keyboardShouldPersistTaps="handled"
                                            onEndReached={() => {
                                                if (!isLoading && !isFetchingNextPage && hasNextPage) fetchNextPage();
                                            }}
                                            refreshing={false}
                                            onRefresh={() => queryClient.invalidateQueries({ queryKey: QueryKeys.trainingModules.all() })}
                                            ListFooterComponent={!isLoading
                                                ? <View style={{ paddingBottom: 80 }}>
                                                    {sortedModules.length === 0 && <EmptyListPlaceholder icon="school-outline" spacing={10} message="No training modules yet!" />}
                                                </View>
                                                : <View style={{ alignContent: 'center', justifyContent: 'center' }}>
                                                    <ActivityIndicator size="large" color={colors.primary} />
                                                </View>
                                            }
                                        />
                                    </View>
                                </View>
                            );
                        default:
                            return null;
                    }
                }}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    container: { flex: 1 },
    listContainer: { flex: 1, width: "100%" },
    listContent: (isLandscape) => ({
        flex: 1,
        padding: spacing.md,
        maxWidth: isLandscape ? 800 : undefined,
        alignSelf: isLandscape ? 'center' : undefined,
        width: '100%',
    }),
    searchBar: {
        marginBottom: spacing.md,
        gap: spacing.xs,
    },
    overviewContainer: {
        flex: 1,
        padding: spacing.lg,
    },
    overviewTitle: {
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color: colors.text,
        marginBottom: spacing.xs,
    },
    overviewSubtitle: {
        fontSize: typography.body,
        color: colors.textSecondary,
        marginBottom: spacing.lg,
    },
    statsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.md,
    },
    statCard: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: spacing.lg,
        minWidth: 150,
        flex: 1,
        alignItems: 'center',
    },
    statValue: {
        fontSize: 28,
        fontFamily: fonts.heading,
        color: colors.primary,
        marginBottom: spacing.xs,
    },
    statLabel: {
        fontSize: typography.caption,
        color: colors.textSecondary,
        textAlign: 'center',
    },
}));
